import { NavLink } from "react-router-dom";
import "./Navbar.css";
import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";

const userNameFields = [
  "displayName",
  "name",
  "fullName",
  "userName",
  "username",
  "preferred_username",
];

const preferredStorageKeys = [
  "displayName",
  "name",
  "userName",
  "username",
  "userProfile",
  "profile",
  "user",
  "account",
  "activeAccount",
];

const getNameFromObject = (value) => {
  if (!value || typeof value !== "object") {
    return null;
  }

  for (const field of userNameFields) {
    const candidate = value[field];

    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }

  return null;
};

const parseStoredName = (rawValue) => {
  if (!rawValue || typeof rawValue !== "string") {
    return null;
  }

  const trimmedValue = rawValue.trim();

  if (!trimmedValue) {
    return null;
  }

  if (!trimmedValue.startsWith("{") && !trimmedValue.startsWith("[")) {
    return trimmedValue;
  }

  try {
    const parsedValue = JSON.parse(trimmedValue);

    if (Array.isArray(parsedValue)) {
      for (const entry of parsedValue) {
        const candidate = getNameFromObject(entry);

        if (candidate) {
          return candidate;
        }
      }

      return null;
    }

    return getNameFromObject(parsedValue);
  } catch {
    return null;
  }
};

const getNameFromStorage = (storage) => {
  if (!storage) {
    return null;
  }

  for (const key of preferredStorageKeys) {
    const candidate = parseStoredName(storage.getItem(key));

    if (candidate) {
      return candidate;
    }
  }

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);

    if (!key) {
      continue;
    }

    const normalizedKey = key.toLowerCase();

    if (
      !normalizedKey.includes("msal") &&
      !normalizedKey.includes("account") &&
      !normalizedKey.includes("profile") &&
      !normalizedKey.includes("user")
    ) {
      continue;
    }

    const candidate = parseStoredName(storage.getItem(key));

    if (candidate) {
      return candidate;
    }
  }

  return null;
};

const resolveUserName = () => {
  if (typeof window === "undefined") {
    return "Local User";
  }

  return (
    getNameFromStorage(window.localStorage) ||
    getNameFromStorage(window.sessionStorage) ||
    "Local User"
  );
};

const navItems = [
  { label: "Home", path: "/" },
  { label: "How do I choose a cluster", path: "/choose-cluster" },
  { label: "Capabilities", path: "/capabilities" },
  { label: "Calendar", path: "/calendar" },
  { label: "FAQs", path: "/faq" },
];

const Navbar = () => {
  const { accounts, instance } = useMsal();
  const [name, setName] = useState(resolveUserName);

  useEffect(() => {
    const activeAccount = instance.getActiveAccount() || accounts[0] || null;

    const syncUserName = () => {
      if (activeAccount?.name) {
        if (!instance.getActiveAccount()) {
          instance.setActiveAccount(activeAccount);
        }

        setName(activeAccount.name);
        return;
      }

      setName(resolveUserName());
    };

    syncUserName();
    window.addEventListener("storage", syncUserName);

    return () => {
      window.removeEventListener("storage", syncUserName);
    };
  }, [accounts, instance]);

  return (
    <header className="navbar">
      <div className="navbar-left">
        <img
          src={process.env.PUBLIC_URL + "/Images/Accenture-logo.png"}
          alt="Logo"
          className="navbar-logo"
        />
        <span className="navbar-title">Quantum Reinvent Program</span>
      </div>

      <nav className="navbar-center">
        {navItems.map(({ label, path }) => (
          <NavLink
            key={path}
            to={path}
            end
            className={({ isActive }) =>
              `nav-link ${isActive ? "nav-link-active" : ""}`
            }
          >
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="navbar-right">
        <span className="navbar-user">Hello, {name}!</span>
        <img
          src={process.env.PUBLIC_URL + "/Images/Avatar.png"}
          alt="User"
          className="navbar-avatar"
        />
      </div>
    </header>
  );
};

export default Navbar;
